import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { AxiosError } from 'axios';
import { UserRegistrationForm, UserRegisterState } from './userRegisterTypes';
import { useTypedSelector } from '../../store';
import * as VseAlert from '../alert';

const msg404Activate = 'The account activation link is invalid or has been expired';

const initialState = {
  loading: 'idle',
  emailConfirmation: 'idle',
} as UserRegisterState;

export const register = createAsyncThunk('userRegister/register', async (f: UserRegistrationForm, thunkApi) => {
  const data = {
    userName: f.userName,
    homeUniversityID: f.homeUniversityID,
    password: f.password,
    confirmPassword: f.confirmPassword,
  };
  try {
    const response = await axios.post('api/identity/register', data);
    return response;
  } catch (err) {
    const error: AxiosError = err;
    VseAlert.handleThunkError(thunkApi, error);
    return thunkApi.rejectWithValue(error);
  }
});

export const confirmEmail = createAsyncThunk('userRegister/confirmEmail', async ({ encodedId, encodedCode }: { encodedId: string, encodedCode: string }, thunkApi) => {
  let data = new FormData();
  data.append('id', encodedId);
  data.append('token', encodedCode);
  try {
    const response = await axios.post('api/identity/confirmEmail', data);
    return response;
  } catch (err) {
    const error: AxiosError = err;
    VseAlert.handleThunkError(thunkApi, error, msg404Activate);
    return thunkApi.rejectWithValue(error);
  }
});

export const userRegisterSlice = createSlice({
  name: 'userRegister',
  initialState,
  reducers: {
    reset: (state) => {
      state = Object.assign(state, initialState);
    },
  },
  extraReducers: builder => {
    builder.addCase(register.pending, (state) => {
      state.loading = 'loading';
    });
    builder.addCase(register.fulfilled, (state) => {
      state.loading = 'succeeded';
    });
    builder.addCase(register.rejected, (state) => {
      state.loading = 'pending';
    });
    builder.addCase(confirmEmail.pending, (state) => {
      state.emailConfirmation = 'pending';
    });
    builder.addCase(confirmEmail.fulfilled, (state) => {
      state.emailConfirmation = 'succeeded';
    });
    builder.addCase(confirmEmail.rejected, (state) => {
      state.emailConfirmation = 'failed';
    });
  },
});

export const { reset } = userRegisterSlice.actions;

export default userRegisterSlice.reducer;

export const useUserRegisterState = (): UserRegisterState => useTypedSelector(state => state.userRegister);
